// True-to-scale comparison of the poster sizes a product is sold in,
// with a phone outline for a sense of real-world size.
const PAPER = {
  A5: { w: 14.8, h: 21.0 },
  A4: { w: 21.0, h: 29.7 },
  A3: { w: 29.7, h: 42.0 },
  A2: { w: 42.0, h: 59.4 },
  A1: { w: 59.4, h: 84.1 },
};
const PHONE = { w: 7.5, h: 15.5 }; // a typical smartphone, in cm
const inch = (cm) => (cm / 2.54).toFixed(1);

const SizeGuide = ({ sizes, selected }) => {
  const known = (sizes || [])
    .map((s) => ({ ...s, key: String(s.label).toUpperCase().trim() }))
    .filter((s) => PAPER[s.key]);
  const list = known.length > 0 ? known : [{ key: 'A4' }, { key: 'A3' }];
  const sorted = [...list].sort((a, b) => PAPER[a.key].h - PAPER[b.key].h);
  const largest = PAPER[sorted[sorted.length - 1].key];

  // drawing scale: largest poster is 180 units tall
  const k = 180 / largest.h;
  const pad = 14;
  const phoneW = PHONE.w * k;
  const width = largest.w * k + phoneW + pad * 3 + 10;
  const height = largest.h * k + pad * 2 + 6;
  const baseY = pad + largest.h * k; // everything stands on the same baseline
  const selectedKey = selected ? String(selected.label).toUpperCase().trim() : null;

  return (
    <div className="border-t border-border/40 pt-6" data-testid="size-guide">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-heading font-bold text-lg text-foreground">Size Guide</h3>
        <p className="text-xs text-muted-foreground">Drawn to scale</p>
      </div>
      <div className="bg-secondary border border-border/40 rounded-sm p-4 flex flex-col sm:flex-row gap-4 items-center">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-full max-w-[260px] h-auto flex-shrink-0"
          role="img"
          aria-label={`Poster size comparison: ${sorted.map((s) => s.key).join(', ')} next to a phone`}
        >
          {[...sorted].reverse().map((s) => {
            const p = PAPER[s.key];
            const w = p.w * k;
            const h = p.h * k;
            const isSel = selectedKey === s.key;
            return (
              <g key={s.key}>
                <rect
                  x={pad}
                  y={baseY - h}
                  width={w}
                  height={h}
                  rx="2"
                  fill={isSel ? 'rgba(255,255,255,0.16)' : 'rgba(255,255,255,0.05)'}
                  stroke={isSel ? '#ffffff' : 'rgba(255,255,255,0.45)'}
                  strokeWidth={isSel ? 2 : 1.2}
                  strokeDasharray={isSel ? '0' : '4 3'}
                />
                <text
                  x={pad + 6}
                  y={baseY - h + 16}
                  fill="#ffffff"
                  fontSize="12"
                  fontWeight="700"
                  fontFamily="inherit"
                >
                  {s.key}
                </text>
              </g>
            );
          })}
          {/* phone for scale */}
          <g>
            <rect
              x={pad * 2 + largest.w * k + 10}
              y={baseY - PHONE.h * k}
              width={phoneW}
              height={PHONE.h * k}
              rx="4"
              fill="rgba(255,255,255,0.08)"
              stroke="rgba(255,255,255,0.6)"
              strokeWidth="1.2"
            />
            <rect
              x={pad * 2 + largest.w * k + 10 + phoneW / 2 - 4}
              y={baseY - PHONE.h * k + 3}
              width="8"
              height="2"
              rx="1"
              fill="rgba(255,255,255,0.6)"
            />
            <text
              x={pad * 2 + largest.w * k + 10 + phoneW / 2}
              y={baseY - PHONE.h * k - 6}
              fill="rgba(255,255,255,0.7)"
              fontSize="9"
              textAnchor="middle"
              fontFamily="inherit"
            >
              Phone
            </text>
          </g>
          <line x1={pad - 4} x2={width - pad + 4} y1={baseY + 1} y2={baseY + 1} stroke="rgba(255,255,255,0.25)" />
        </svg>

        <ul className="w-full space-y-2 text-sm">
          {sorted.map((s) => {
            const p = PAPER[s.key];
            const isSel = selectedKey === s.key;
            return (
              <li
                key={s.key}
                className={`flex items-center justify-between gap-3 rounded-sm px-3 py-2 border ${
                  isSel ? 'border-white bg-white/10' : 'border-border/40'
                }`}
              >
                <span className="font-heading font-bold text-foreground">{s.key}</span>
                <span className="text-foreground/80 text-right">
                  {p.w} × {p.h} cm
                  <span className="block text-[11px] text-muted-foreground">
                    {inch(p.w)} × {inch(p.h)} in
                  </span>
                </span>
              </li>
            );
          })}
          {sorted.some((s) => s.key === 'A3') && sorted.some((s) => s.key === 'A4') && (
            <li className="text-[11px] text-muted-foreground px-1">A3 is double the area of A4.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default SizeGuide;
